import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RARE_ITEMS, levelsPriceRcd } from "@redchain/contracts";
import { api } from "../api/client";
import { sseBus } from "../api/events";
import type { ItemRow, MonSummary } from "../api/types";
import { ActionStatus } from "../components/ActionStatus";
import { formatSol } from "../overlay/format";
import { useWalletAction } from "../overlay/useWalletAction";
import { t } from "../strings";
import { useWallet } from "../wallet/WalletProvider";

function BuyLevels({ mon, name, rcdPerSol, onChanged }: { mon: MonSummary; name: string; rcdPerSol: bigint; onChanged: () => void }) {
  const action = useWalletAction();
  const [target, setTarget] = useState(Math.min(100, mon.level + 1));
  const quote = target > mon.level ? levelsPriceRcd(mon.level, target, rcdPerSol).toString() : null;

  const onBuy = async () => {
    const built = await api.buyLevels(mon.asset, target).catch((e: unknown) => {
      window.alert(e instanceof Error ? e.message : String(e));
      return null;
    });
    if (!built || !window.confirm(t.buyLevelsConfirm(name, built.fromLevel, built.toLevel, built.rcd))) return;
    if (await action.run(async () => built)) onChanged();
  };

  return (
    <div>
      <div className="row">
        <input
          type="number"
          min={mon.level + 1}
          max={100}
          value={target}
          onChange={(e) => setTarget(Math.max(mon.level + 1, Math.min(100, Number(e.target.value) || mon.level + 1)))}
          aria-label="Target level"
        />
        <button disabled={action.busy || !quote} onClick={onBuy}>
          Buy levels
        </button>
      </div>
      {quote && <p className="small">{t.buyLevelsQuote(mon.level, target, quote)}</p>}
      <p className="small hint">{t.buyLevelsHelp}</p>
      <ActionStatus {...action} />
    </div>
  );
}

function MonCard({ mon, rcdPerSol, onChanged }: { mon: MonSummary; rcdPerSol: bigint; onChanged: () => void }) {
  const burn = useWalletAction();
  const transfer = useWalletAction();
  const [to, setTo] = useState("");
  const [showTransfer, setShowTransfer] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const name = mon.nickname || mon.speciesName || `#${mon.dex}`;
  const canBuyLevels = rcdPerSol > 0n && mon.level < 100 && mon.status === "active";

  const onBurn = async (refundAs: "rcd" | "sol") => {
    // The refund shown comes from the server's build response, not a client-side rent estimate.
    const built = await api.burnMon(mon.asset, refundAs).catch((e: unknown) => {
      window.alert(e instanceof Error ? e.message : String(e));
      return null;
    });
    if (!built) return;
    const ok =
      refundAs === "rcd" && built.refundRcd
        ? window.confirm(t.burnConfirmRcd(name, built.refundRcd))
        : window.confirm(t.burnConfirm(name, built.refundLamports ?? mon.refundableLamports));
    if (!ok) return;
    if (await burn.run(async () => built)) onChanged();
  };

  const onTransfer = async () => {
    if (!to) return;
    if (await transfer.run(() => api.relayBuild("nft_transfer", { asset: mon.asset, to }))) {
      setShowTransfer(false);
      onChanged();
    }
  };

  return (
    <li className="mon">
      <img src={mon.image} alt="" width={96} height={96} loading="lazy" />
      <div>
        <strong>{name}</strong> <span className="hint">
          {mon.speciesName} · Lv {mon.level}
        </span>
        <p className="small">
          HP {mon.stats.maxHp} · Atk {mon.stats.attack} · Def {mon.stats.defense} · Spd {mon.stats.speed} · Spc {mon.stats.special}
        </p>
        <p className="small">
          {mon.refundRcd
            ? mon.solRefundAllowed
              ? t.refundRcdHint(mon.refundRcd, mon.refundableLamports)
              : t.refundRcdOnly(mon.refundRcd)
            : `Refundable ${formatSol(mon.refundableLamports)} SOL`}
        </p>
        {mon.status !== "active" && <p className="small hint">{mon.status.replace(/_/g, " ")}</p>}
        <div className="row">
          {mon.refundRcd && (
            <button className="danger" disabled={burn.busy} onClick={() => onBurn("rcd")}>
              Burn for ₽
            </button>
          )}
          {mon.solRefundAllowed && (
            <button className="danger" disabled={burn.busy} onClick={() => onBurn("sol")}>
              {mon.refundRcd ? "Burn for SOL" : "Burn"}
            </button>
          )}
          <button onClick={() => setShowTransfer((v) => !v)}>Transfer</button>
          {canBuyLevels && <button onClick={() => setShowLevels((v) => !v)}>Level up</button>}
        </div>
        {showLevels && canBuyLevels && <BuyLevels mon={mon} name={name} rcdPerSol={rcdPerSol} onChanged={onChanged} />}
        {showTransfer && (
          <div className="row">
            <input placeholder="Recipient wallet address" value={to} onChange={(e) => setTo(e.target.value.trim())} />
            <button disabled={transfer.busy || !to} onClick={onTransfer}>
              Send
            </button>
          </div>
        )}
        <ActionStatus {...burn} />
        <ActionStatus {...transfer} />
      </div>
    </li>
  );
}

function ItemTransfer({ item, onChanged }: { item: ItemRow; onChanged: () => void }) {
  const action = useWalletAction();
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("1");
  if (item.wallet <= 0) return null;
  return (
    <div className="row">
      <input placeholder="Recipient" value={to} onChange={(e) => setTo(e.target.value.trim())} />
      <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "") || "1")} aria-label="Amount" />
      <button
        disabled={action.busy || !to}
        onClick={async () => {
          if (await action.run(() => api.relayBuild("item_transfer", { index: item.index, amount: Number(amount), to }))) onChanged();
        }}
      >
        Send
      </button>
      <ActionStatus {...action} />
    </div>
  );
}

/** /collection — owned Pokémon and items, burn with refund confirmation, transfer (WEB-17). */
export function Collection() {
  const { wallet, auth } = useWallet();
  const [mons, setMons] = useState<MonSummary[] | null>(null);
  const [rcdPerSol, setRcdPerSol] = useState(0n);
  const [items, setItems] = useState<ItemRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wallet) return;
    try {
      const [m, i] = await Promise.all([api.monsWithRate(wallet.address), auth ? api.items().then((r) => r.items) : Promise.resolve([])]);
      setMons(m.mons);
      setRcdPerSol(auth ? BigInt(m.rcdPerSol ?? "0") : 0n);
      setItems(i);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [wallet, auth]);

  useEffect(() => {
    void load();
    const offs = (["chain.committed", "ownership.changed", "mon.refunded", "items.changed", "mon.levels_bought"] as const).map((type) => sseBus.on(type, () => void load()));
    return () => offs.forEach((off) => off());
  }, [load]);

  if (!wallet)
    return (
      <main className="page">
        <Link to="/">Log in</Link> to see your collection.
      </main>
    );

  return (
    <main className="page collection">
      <h1>Collection</h1>
      {error && <p className="status error">{error}</p>}
      <h2>Pokémon</h2>
      {mons === null && !error && <p>Loading…</p>}
      {mons?.length === 0 && <p>No Pokémon on chain yet. Catch one and it mints within seconds of the auto-save.</p>}
      <ul className="mons">
        {mons?.map((m) => <MonCard key={m.asset} mon={m} rcdPerSol={rcdPerSol} onChanged={load} />)}
      </ul>
      <h2>Rare items</h2>
      <ul className="items">
        {items?.map((i) => (
          <li key={i.index}>
            <strong>{i.name || RARE_ITEMS[i.index]?.name}</strong> — {Math.max(0, i.inGame + i.credit - i.debt)} in game · {i.wallet} in wallet
            <ItemTransfer item={i} onChanged={load} />
          </li>
        ))}
      </ul>
    </main>
  );
}
